import React, { useState, useEffect } from 'react';
import { Cliente, eliminarCliente } from '../services/clienteService';
import { Planta } from '../services/plantaService';
import { Laboratorio, eliminarLaboratorio, updateLaboratorio } from '../services/laboratorioService';
import {
  getClientesConPlantas,
  getLaboratoriosConUsuarios,
  ClienteConPlantas,
  LaboratorioConUsuarios,
} from '../services/usuarioService';
import { hasTestManagementPermission, hasUserManagementPermission } from '../services/authService';
import { deleteEnsayo, deleteEnsayoRegulado, EnsayoGestion, EnsayoRegulado, getEnsayosGestion } from '../services/ensayoService';
import PlantaForm from './PlantaForm';
import ClienteForm from './ClienteForm';
import LaboratorioForm from './LaboratorioForm';
import CrearUsuarioForm, { OrganizacionInicial } from './CrearUsuarioForm';
import UsuariosList from './UsuariosList';
import EnsayoForm from './EnsayoForm';
import EnsayoReguladoForm from './EnsayoReguladoForm';
import { Badge, Button, Card, Checkbox, EmptyState, LoadingState } from './ui';
import {
  BuildingOffice2Icon,
  BeakerIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

interface GestionProps {}

// New main and sub tab types
type MainTab = 'gestion-usuarios' | 'creacion-usuarios' | 'gestion-ensayos';
type UserTab = 'usuarios' | 'clientes' | 'laboratorios';
type EnsayosTab = 'ensayos' | 'ensayos-regulados';

const Gestion: React.FC<GestionProps> = () => {
  const canManageUsers = hasUserManagementPermission();
  const canManageTests = hasTestManagementPermission();
  // Top-level tab
  const [mainTab, setMainTab] = useState<MainTab>(
    canManageUsers ? 'gestion-usuarios' : 'gestion-ensayos'
  );
  // Inner tabs
  const [userTab, setUserTab] = useState<UserTab>('usuarios');
  const [ensayosTab, setEnsayosTab] = useState<EnsayosTab>('ensayos');
  
  const [clientesConPlantas, setClientesConPlantas] = useState<ClienteConPlantas[]>([]);
  const [laboratoriosConUsuarios, setLaboratoriosConUsuarios] = useState<LaboratorioConUsuarios[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ensayos, setEnsayos] = useState<EnsayoGestion[]>([]);
  const [editingEnsayo, setEditingEnsayo] = useState<EnsayoGestion | null>(null);
  const [showEnsayoForm, setShowEnsayoForm] = useState(false);
  const [editingRegulado, setEditingRegulado] = useState<EnsayoRegulado | null>(null);
  const [showReguladoForm, setShowReguladoForm] = useState(false);
  const [filtrosEnsayo, setFiltrosEnsayo] = useState({ medio: '', tipo: '', unidad: '' });

  const [editingPlanta, setEditingPlanta] = useState<Planta | null>(null);
  const [clienteDePlanta, setClienteDePlanta] = useState<string>('');
  // Organización sobre la que se está dando de alta un usuario desde su ficha.
  const [organizacionParaUsuario, setOrganizacionParaUsuario] = useState<OrganizacionInicial | undefined>(undefined);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [editingLaboratorio, setEditingLaboratorio] = useState<Laboratorio | null>(null);

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clientesConPlantasData, laboratoriosConUsuariosData, ensayosData] = await Promise.all([
        canManageUsers ? getClientesConPlantas() : Promise.resolve([]),
        canManageUsers ? getLaboratoriosConUsuarios() : Promise.resolve([]),
        canManageTests ? getEnsayosGestion() : Promise.resolve([]),
      ]);
      setClientesConPlantas(clientesConPlantasData as ClienteConPlantas[]);
      setLaboratoriosConUsuarios(laboratoriosConUsuariosData as LaboratorioConUsuarios[]);
      setEnsayos(ensayosData as EnsayoGestion[]);
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar los datos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const filteredClientes = clientesConPlantas.filter(({ cliente, plantas }) => {
    const q = searchTerm.toLowerCase();
    return (
      (cliente.nombre?.toLowerCase().includes(q) || cliente.razon_social?.toLowerCase().includes(q) || cliente.cuit?.toLowerCase().includes(q)) ||
      plantas.some(p => p.denominacion?.toLowerCase().includes(q))
    );
  });

  const filteredLaboratorios = laboratoriosConUsuarios.filter(({ laboratorio }) => {
    const q = searchTerm.toLowerCase();
    return (
      laboratorio.nombre?.toLowerCase().includes(q) || laboratorio.cuit?.toLowerCase().includes(q) || laboratorio.email?.toLowerCase().includes(q)
    );
  });

  const nombreMedio = (ensayo: EnsayoGestion) => ensayo.medio_nombre || `Medio ${ensayo.medio}`;

  // Valores disponibles para los filtros de cabecera, sin repetir y ordenados.
  const opcionesDe = (obtener: (ensayo: EnsayoGestion) => string) =>
    Array.from(new Set(ensayos.map(obtener).filter(valor => valor.trim() !== ''))).sort((a, b) =>
      a.localeCompare(b, 'es')
    );

  const opcionesMedio = opcionesDe(nombreMedio);
  const opcionesTipo = opcionesDe(ensayo => ensayo.tipo);
  const opcionesUnidad = opcionesDe(ensayo => ensayo.unidad);

  const filteredEnsayos = ensayos.filter(ensayo => {
    const q = searchTerm.toLowerCase();
    const coincideBusqueda = ensayo.analito.toLowerCase().includes(q)
      || ensayo.tipo.toLowerCase().includes(q)
      || ensayo.unidad.toLowerCase().includes(q)
      || nombreMedio(ensayo).toLowerCase().includes(q);

    return coincideBusqueda
      && (filtrosEnsayo.medio === '' || nombreMedio(ensayo) === filtrosEnsayo.medio)
      && (filtrosEnsayo.tipo === '' || ensayo.tipo === filtrosEnsayo.tipo)
      && (filtrosEnsayo.unidad === '' || ensayo.unidad === filtrosEnsayo.unidad);
  });

  // Filtros activos de la tabla de ensayos, incluido el buscador de texto.
  const hayFiltrosDeEnsayo =
    searchTerm.trim() !== '' || filtrosEnsayo.medio !== '' || filtrosEnsayo.tipo !== '' || filtrosEnsayo.unidad !== '';

  const limpiarFiltrosDeEnsayo = () => {
    setSearchTerm('');
    setFiltrosEnsayo({ medio: '', tipo: '', unidad: '' });
  };

  const filteredRegulados = ensayos.flatMap(ensayo => ensayo.regulados.map(regulado => ({ regulado, ensayo }))).filter(({ regulado, ensayo }) => {
    const q = searchTerm.toLowerCase();
    return ensayo.analito.toLowerCase().includes(q) || (regulado.marco_nombre || '').toLowerCase().includes(q);
  });

  const handleEditEnsayo = (ensayo: EnsayoGestion) => {
    const regulated = ensayo.regulados[0];
    const controlled = ensayo.controlados[0];
    setEditingEnsayo({
      ...ensayo,
      regulado_id: regulated?.id,
      id_marco: regulated?.id_marco,
      marco_nombre: regulated?.marco_nombre,
      valor_min: regulated?.valor_min,
      valor_max: regulated?.valor_max,
      valor_ref: regulated?.valor_ref,
      presencia: regulated?.presencia,
      controlado_id: controlled?.id,
      id_organismo: controlled?.id_organismo,
      organismo_nombre: controlled?.organismo_nombre,
      metodo_deteccion: controlled?.metodo_deteccion,
      limite_deteccion: controlled?.limite_deteccion,
    });
    setShowEnsayoForm(true);
  };

  const handleDeleteEnsayo = async (ensayo: EnsayoGestion) => {
    if (!window.confirm(`¿Eliminar el ensayo "${ensayo.analito}"?`)) return;
    try {
      await deleteEnsayo(ensayo.id);
      await cargarDatos();
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo eliminar el ensayo.');
    }
  };

  if (!canManageUsers && !canManageTests) {
    return (
      <div className="text-center p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-lg font-medium text-red-800 mb-2">Acceso Denegado</h3>
          <p className="text-red-700">No tienes permisos para acceder a esta sección.</p>
        </div>
      </div>
    );
  }

  const handleDeleteRegulado = async (regulado: EnsayoRegulado) => {
    if (!window.confirm('¿Eliminar esta regulación del ensayo?')) return;
    try {
      await deleteEnsayoRegulado(regulado.id);
      await cargarDatos();
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo eliminar el ensayo regulado.');
    }
  };

  const upsertPlantaForCliente = async (clienteId: number) => {
    const entry = clientesConPlantas.find(c => c.cliente.id === clienteId);
    if (!entry) return;
    // Plantilla vacía: el formulario la reconoce como alta porque no trae id.
    setEditingPlanta({ id_cliente: clienteId, denominacion: '', direccion: '' } as Planta);
    setClienteDePlanta(entry.cliente.nombre);
  };

  const editarPlanta = (planta: Planta, nombreCliente: string) => {
    setEditingPlanta({ ...planta });
    setClienteDePlanta(nombreCliente);
  };

  // Alta de un usuario sobre un cliente o laboratorio que ya existe: se abre el
  // formulario de creación con la organización ya elegida.
  const crearUsuarioPara = (tipo: 'cliente' | 'laboratorio', id: number) => {
    setOrganizacionParaUsuario({ tipo, id });
    setMainTab('creacion-usuarios');
  };

  // Eliminar una organización es una baja lógica: el cliente o laboratorio y
  // todas las cuentas asignadas pasan a estado 2. Dejan de verse en la
  // aplicación, pero siguen en la base para no romper el historial de análisis.
  const eliminarOrganizacion = async (
    tipo: 'clientes' | 'laboratorios',
    id: number,
    nombre: string
  ) => {
    const etiqueta = tipo === 'clientes' ? 'el cliente' : 'el laboratorio';
    const confirmacion =
      `¿Confirma que desea eliminar ${etiqueta} "${nombre}"?\n\n` +
      'Tanto la organización como todos sus usuarios dejarán de aparecer en la aplicación ' +
      'y no podrán iniciar sesión. Los análisis ya cargados se conservan.';
    if (!window.confirm(confirmacion)) return;

    try {
      await (tipo === 'clientes' ? eliminarCliente(id) : eliminarLaboratorio(id));
      await cargarDatos();
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo eliminar la organización.');
    }
  };

  const togglePermisoCliente = async (
    cliente: any,
    key: 'p_busqueda' | 'p_carga' | 'p_modif_analisis' | 'p_nuevo_analisis'
  ) => {
    try {
      const nuevo = { [key]: !Boolean(cliente[key]) } as any;
      const { updateCliente } = await import('../services/clienteService');
      await updateCliente(cliente.id!, nuevo);
      cargarDatos();
    } catch (e) { console.error(e); }
  };

  const togglePermisoLaboratorio = async (
    lab: any,
    key: 'p_busqueda' | 'p_carga' | 'p_modif_analisis' | 'p_nuevo_analisis'
  ) => {
    try {
      const nuevo = { [key]: !Boolean(lab[key]) } as any;
      await updateLaboratorio(lab.id!, nuevo);
      cargarDatos();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <LoadingState label="Cargando gestión..." />;
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-lg font-medium text-red-800 mb-2">Error</h3>
          <p className="text-red-700">{error}</p>
          <Button variant="danger" onClick={cargarDatos} className="mt-4">Reintentar</Button>
        </div>
      </div>
    );
  }

  const mainTabClasses = (active: boolean) =>
    `py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      active ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
    }`;

  const subTabClasses = (active: boolean) =>
    `py-2 px-3 rounded-md font-medium text-sm transition-colors ${
      active ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
    }`;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-card">
      <div className="p-6 border-b border-slate-200">
        <h2 className="text-2xl font-semibold text-slate-900">Gestión</h2>
        <p className="text-slate-500 mt-1">Administración central</p>
      </div>

      {/* Main tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8 px-6">
          {canManageUsers && <button className={mainTabClasses(mainTab === 'gestion-usuarios')} onClick={() => setMainTab('gestion-usuarios')}>Gestión de Usuarios</button>}
          {canManageUsers && <button className={mainTabClasses(mainTab === 'creacion-usuarios')} onClick={() => { setOrganizacionParaUsuario(undefined); setMainTab('creacion-usuarios'); }}>Creación de Usuarios</button>}
          {canManageTests && <button className={mainTabClasses(mainTab === 'gestion-ensayos')} onClick={() => setMainTab('gestion-ensayos')}>Gestión de Ensayos</button>}
        </nav>
      </div>

      <div className="p-6">
        {/* Gestión de Usuarios */}
        {mainTab === 'gestion-usuarios' && (
          <>
            {/* Sub tabs for users */}
            <div className="flex gap-2 mb-4">
              <button className={subTabClasses(userTab === 'usuarios')} onClick={() => setUserTab('usuarios')}>Usuarios y claves</button>
              <button className={subTabClasses(userTab === 'clientes')} onClick={() => setUserTab('clientes')}>Clientes</button>
              <button className={subTabClasses(userTab === 'laboratorios')} onClick={() => setUserTab('laboratorios')}>Laboratorios</button>
            </div>

            {userTab !== 'usuarios' && <div className="mb-4 relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nombre, CUIT o email..." className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-md text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
            </div>}

            {userTab === 'usuarios' && <UsuariosList onCreateNew={() => setMainTab('creacion-usuarios')} />}

            {userTab === 'clientes' && (
              filteredClientes.length === 0 ? (
                <EmptyState title="No se encontraron clientes" description="Ajustá la búsqueda o creá un nuevo cliente." />
              ) : (
              <div className="space-y-4">
                {filteredClientes.map(({ cliente, plantas }, index) => (
                  <Card key={`cliente-${cliente.id}-${index}`} className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <BuildingOffice2Icon className="h-5 w-5 text-slate-400" />
                          <div>
                            <div className="font-semibold text-slate-900">{cliente.nombre}</div>
                            <div className="text-sm text-slate-500">{cliente.razon_social}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-slate-600">
                          <div>CUIT: {cliente.cuit}</div>
                          <div>Email: {cliente.email}</div>
                          <div>Tel: {cliente.telefono}</div>
                          <div>Dir: {cliente.direccion}, {cliente.ciudad}</div>
                          <div>
                            Estado: <Badge tone={cliente.estado === 1 ? 'danger' : 'success'} className="ml-1">{cliente.estado === 1 ? 'Inactivo' : 'Activo'}</Badge>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="text-sm font-medium text-slate-700 mb-1">Permisos</div>
                          <div className="flex flex-wrap gap-4">
                            <Checkbox label="Búsqueda" checked={!!cliente.p_busqueda} onChange={() => togglePermisoCliente(cliente, 'p_busqueda')} />
                            <Checkbox label="Carga" checked={!!cliente.p_carga} onChange={() => togglePermisoCliente(cliente, 'p_carga')} />
                            <Checkbox label="Modif. análisis" checked={!!cliente.p_modif_analisis} onChange={() => togglePermisoCliente(cliente, 'p_modif_analisis')} />
                            <Checkbox label="Nuevo análisis" checked={!!cliente.p_nuevo_analisis} onChange={() => togglePermisoCliente(cliente, 'p_nuevo_analisis')} />

                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <Button size="sm" onClick={() => setEditingCliente(cliente)}>Editar Cliente</Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<UserPlusIcon className="h-4 w-4" />}
                          onClick={() => crearUsuarioPara('cliente', cliente.id!)}
                        >
                          Crear usuario
                        </Button>
                        <Button size="sm" variant="secondary" icon={<PlusIcon className="h-4 w-4" />} onClick={() => upsertPlantaForCliente(cliente.id!)}>
                          Crear Planta
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<TrashIcon className="h-4 w-4" />}
                          onClick={() => eliminarOrganizacion('clientes', cliente.id!, cliente.nombre)}
                        >
                          Eliminar Cliente
                        </Button>
                      </div>
                    </div>
                    {/* Plantas asociadas */}
                    <div className="mt-3 border-t border-slate-200 pt-3">
                      <div className="text-sm font-medium text-slate-700 mb-1">
                        Plantas asociadas ({plantas.length})
                      </div>
                      {plantas.length > 0 ? (
                        <div className="space-y-1">
                          {plantas.map(planta => (
                            <div key={planta.id} className="flex items-center gap-3 text-sm text-slate-700">
                              <BeakerIcon className="h-4 w-4 text-slate-400" />
                              <span>{planta.denominacion}</span>
                              {planta.direccion && <span className="text-slate-500">— {planta.direccion}</span>}
                              <button
                                type="button"
                                className="text-xs text-brand-700 hover:underline"
                                onClick={() => editarPlanta(planta, cliente.nombre)}
                              >
                                Editar
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500 italic">Sin plantas asociadas</div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
              )
            )}

            {userTab === 'laboratorios' && (
              filteredLaboratorios.length === 0 ? (
                <EmptyState title="No se encontraron laboratorios" description="Ajustá la búsqueda o creá un nuevo laboratorio." />
              ) : (
              <div className="space-y-4">
                {filteredLaboratorios.map(({ laboratorio }, index) => (
                  <Card key={`laboratorio-${laboratorio.id}-${index}`} className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <BeakerIcon className="h-5 w-5 text-slate-400" />
                          <div className="font-semibold text-slate-900">{laboratorio.nombre}</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-slate-600">
                          <div>CUIT: {laboratorio.cuit}</div>
                          <div>Email: {laboratorio.email}</div>
                          <div>Tel: {laboratorio.telefono}</div>
                          <div>Dir: {laboratorio.direccion}</div>
                          <div>
                            Estado: <Badge tone={laboratorio.estado === 1 ? 'danger' : 'success'} className="ml-1">{laboratorio.estado === 1 ? 'Inactivo' : 'Activo'}</Badge>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="text-sm font-medium text-slate-700 mb-1">Permisos</div>
                          <div className="flex flex-wrap gap-4">
                            <Checkbox label="Búsqueda" checked={!!laboratorio.p_busqueda} onChange={() => togglePermisoLaboratorio(laboratorio, 'p_busqueda')} />
                            <Checkbox label="Carga" checked={!!laboratorio.p_carga} onChange={() => togglePermisoLaboratorio(laboratorio, 'p_carga')} />
                            <Checkbox label="Modif. análisis" checked={!!laboratorio.p_modif_analisis} onChange={() => togglePermisoLaboratorio(laboratorio, 'p_modif_analisis')} />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <Button size="sm" onClick={() => setEditingLaboratorio(laboratorio)}>Editar Laboratorio</Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<UserPlusIcon className="h-4 w-4" />}
                          onClick={() => crearUsuarioPara('laboratorio', laboratorio.id!)}
                        >
                          Crear usuario
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<TrashIcon className="h-4 w-4" />}
                          onClick={() => eliminarOrganizacion('laboratorios', laboratorio.id!, laboratorio.nombre)}
                        >
                          Eliminar Laboratorio
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              )
            )}
          </>
        )}

        {/* Creación de Usuarios */}
        {mainTab === 'creacion-usuarios' && (
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-slate-900">Crear Nuevo Usuario</h2>
            </div>
            <CrearUsuarioForm
              key={organizacionParaUsuario ? organizacionParaUsuario.tipo + organizacionParaUsuario.id : 'nuevo'}
              organizacionInicial={organizacionParaUsuario}
              onUsuarioCreado={cargarDatos}
            />
          </Card>
        )}

        {/* Gestión de Ensayos */}
        {mainTab === 'gestion-ensayos' && (
          <>
            <div className="flex gap-2 mb-4">
              <button className={subTabClasses(ensayosTab === 'ensayos')} onClick={() => setEnsayosTab('ensayos')}>Ensayos</button>
              <button className={subTabClasses(ensayosTab === 'ensayos-regulados')} onClick={() => setEnsayosTab('ensayos-regulados')}>Ensayos Regulados</button>
            </div>
            <div className="mb-5 flex flex-col gap-3 md:flex-row">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar ensayo o marco regulatorio..." className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-4 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              {ensayosTab === 'ensayos' ? (
                <Button icon={<PlusIcon className="h-4 w-4" />} onClick={() => { setEditingEnsayo(null); setShowEnsayoForm(true); }}>Nuevo ensayo</Button>
              ) : (
                <Button icon={<PlusIcon className="h-4 w-4" />} onClick={() => { setEditingRegulado(null); setShowReguladoForm(true); }}>Nuevo ensayo regulado</Button>
              )}
            </div>
            {ensayosTab === 'ensayos' && (filteredEnsayos.length === 0 ? (
              // Con la tabla vacía desaparece la fila de filtros, así que el
              // botón para limpiarlos tiene que estar acá.
              <EmptyState
                title="No se encontraron ensayos"
                description={
                  hayFiltrosDeEnsayo
                    ? 'Ningún ensayo coincide con los filtros aplicados.'
                    : 'Creá un ensayo nuevo o ajustá la búsqueda.'
                }
                action={
                  hayFiltrosDeEnsayo ? (
                    <Button variant="secondary" size="sm" onClick={limpiarFiltrosDeEnsayo}>
                      Limpiar filtros
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full bg-white text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Analito</th>
                      <th className="px-4 py-3">Medio</th>
                      <th className="px-4 py-3">Tipo / unidad</th>
                      <th className="px-4 py-3">Relaciones</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3">Acciones</th>
                    </tr>
                    <tr className="border-t border-slate-200">
                      <th className="px-4 py-2 text-xs font-normal text-slate-400">Filtrar</th>
                      <th className="px-4 py-2">
                        <select
                          aria-label="Filtrar por medio"
                          value={filtrosEnsayo.medio}
                          onChange={e => setFiltrosEnsayo(f => ({ ...f, medio: e.target.value }))}
                          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs font-normal text-slate-700 focus:border-brand-500 focus:outline-none"
                        >
                          <option value="">Todos</option>
                          {opcionesMedio.map(opcion => <option key={opcion} value={opcion}>{opcion}</option>)}
                        </select>
                      </th>
                      <th className="px-4 py-2">
                        <div className="flex gap-1">
                          <select
                            aria-label="Filtrar por tipo"
                            value={filtrosEnsayo.tipo}
                            onChange={e => setFiltrosEnsayo(f => ({ ...f, tipo: e.target.value }))}
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs font-normal text-slate-700 focus:border-brand-500 focus:outline-none"
                          >
                            <option value="">Tipo: todos</option>
                            {opcionesTipo.map(opcion => <option key={opcion} value={opcion}>{opcion}</option>)}
                          </select>
                          <select
                            aria-label="Filtrar por unidad"
                            value={filtrosEnsayo.unidad}
                            onChange={e => setFiltrosEnsayo(f => ({ ...f, unidad: e.target.value }))}
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs font-normal text-slate-700 focus:border-brand-500 focus:outline-none"
                          >
                            <option value="">Unidad: todas</option>
                            {opcionesUnidad.map(opcion => <option key={opcion} value={opcion}>{opcion}</option>)}
                          </select>
                        </div>
                      </th>
                      <th className="px-4 py-2" colSpan={3}>
                        {(filtrosEnsayo.medio || filtrosEnsayo.tipo || filtrosEnsayo.unidad) && (
                          <button
                            type="button"
                            className="text-xs font-normal text-brand-700 hover:underline"
                            onClick={() => setFiltrosEnsayo({ medio: '', tipo: '', unidad: '' })}
                          >
                            Quitar filtros
                          </button>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>{filteredEnsayos.map(ensayo => (
                    <tr key={ensayo.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-900">{ensayo.analito}</td>
                      <td className="px-4 py-3 text-slate-600">{ensayo.medio_nombre || `Medio ${ensayo.medio}`}</td>
                      <td className="px-4 py-3 text-slate-600">{ensayo.tipo}<div className="text-xs text-slate-400">{ensayo.unidad}</div></td>
                      <td className="px-4 py-3"><div className="flex gap-2"><Badge tone="info">{ensayo.regulados.length} regulados</Badge><Badge tone="neutral">{ensayo.controlados.length} controles</Badge></div></td>
                      <td className="px-4 py-3"><Badge tone={ensayo.estado ? 'success' : 'danger'}>{ensayo.estado ? 'Activo' : 'Inactivo'}</Badge></td>
                      <td className="px-4 py-3"><div className="flex gap-2"><Button size="sm" variant="secondary" icon={<PencilIcon className="h-4 w-4" />} onClick={() => handleEditEnsayo(ensayo)}>Editar</Button><Button size="sm" variant="danger" icon={<TrashIcon className="h-4 w-4" />} onClick={() => handleDeleteEnsayo(ensayo)}>Eliminar</Button></div></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ))}
            {ensayosTab === 'ensayos-regulados' && (filteredRegulados.length === 0 ? (
              <EmptyState
                title="No se encontraron ensayos regulados"
                description={
                  hayFiltrosDeEnsayo
                    ? 'Ninguna regulación coincide con los filtros aplicados.'
                    : 'Creá una regulación nueva o ajustá la búsqueda.'
                }
                action={
                  hayFiltrosDeEnsayo ? (
                    <Button variant="secondary" size="sm" onClick={limpiarFiltrosDeEnsayo}>
                      Limpiar filtros
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full bg-white text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="px-4 py-3">Ensayo</th><th className="px-4 py-3">Marco</th><th className="px-4 py-3">Valores</th><th className="px-4 py-3">Presencia</th><th className="px-4 py-3">Acciones</th></tr></thead>
                  <tbody>{filteredRegulados.map(({ regulado, ensayo }) => (
                    <tr key={regulado.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-900">{ensayo.analito}<div className="text-xs font-normal text-slate-400">{ensayo.unidad}</div></td>
                      <td className="px-4 py-3 text-slate-600">{regulado.marco_nombre || `Marco ${regulado.id_marco}`}</td>
                      <td className="px-4 py-3 text-slate-600"><div>Mín: {regulado.valor_min}</div><div>Máx: {regulado.valor_max}</div><div>Ref: {regulado.valor_ref}</div></td>
                      <td className="px-4 py-3 text-slate-600">{regulado.presencia || '—'}</td>
                      <td className="px-4 py-3"><div className="flex gap-2"><Button size="sm" variant="secondary" onClick={() => { setEditingRegulado(regulado); setShowReguladoForm(true); }}>Editar</Button><Button size="sm" variant="danger" onClick={() => handleDeleteRegulado(regulado)}>Eliminar</Button></div></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ))}
          </>
        )}

      </div>

      {/* Modales */}
      {editingPlanta && (
        <PlantaForm
          planta={editingPlanta}
          nombreCliente={clienteDePlanta}
          onPlantaSaved={() => { setEditingPlanta(null); cargarDatos(); }}
          onCancel={() => setEditingPlanta(null)}
        />
      )}
      {editingCliente && (
        <ClienteForm cliente={editingCliente} onClienteSaved={() => { setEditingCliente(null); cargarDatos(); }} onCancel={() => setEditingCliente(null)} />
      )}
      {editingLaboratorio && (
        <LaboratorioForm laboratorio={editingLaboratorio} onLaboratorioSaved={() => { setEditingLaboratorio(null); cargarDatos(); }} onCancel={() => setEditingLaboratorio(null)} />
      )}
      {showEnsayoForm && (
        <EnsayoForm
          ensayo={editingEnsayo || undefined}
          onEnsayoSaved={() => { setShowEnsayoForm(false); setEditingEnsayo(null); cargarDatos(); }}
          onCancel={() => { setShowEnsayoForm(false); setEditingEnsayo(null); }}
        />
      )}
      {showReguladoForm && (
        <EnsayoReguladoForm
          ensayos={ensayos}
          regulado={editingRegulado || undefined}
          onSaved={() => { setShowReguladoForm(false); setEditingRegulado(null); cargarDatos(); }}
          onCancel={() => { setShowReguladoForm(false); setEditingRegulado(null); }}
        />
      )}
    </div>
  );
};

export default Gestion;
